import * as React from "react";
import { v4 as uuidv4 } from "uuid";
import { linksOf, request } from "../services/cpln";
import moment from "moment";
import { Event } from "../schema/types/event";
import { sortBy } from "lodash";

interface EventTableItem extends Event {
  contextText: string;
  lastEventTimeProcessed: string;
}

export function useEventlogs(link: string) {
  const [eventlogs, setEventlogs] = React.useState<EventTableItem[]>([]);
  const requestIdRef = React.useRef("");

  const [isLoading, setIsLoading] = React.useState(false);
  const [hasFetched, setHasFetched] = React.useState(false);
  const [isFetching, setIsFetching] = React.useState(false);
  const [nextLink, setNextLink] = React.useState<string | undefined>(undefined);
  const [error, setError] = React.useState("");
  const [hasWarning, setHasWarning] = React.useState(false);
  const [itemLastModified, setItemLastModified] = React.useState("");

  React.useEffect(() => {
    fetchEventlogs(true);
  }, []);

  function objectToText(obj: any) {
    let textItems: string[] = [];
    for (let [key, value] of Object.entries(obj)) {
      let localValue = value;
      if (value === null) {
        localValue = "null";
      } else if (typeof value === "object") {
        localValue = objectToText(value).join(", ");
      }
      textItems.push(`${key}=${localValue}`);
    }
    return textItems;
  }

  function toTableItem(eventlog: Event): EventTableItem {
    const contextText = objectToText(eventlog.context).join(", ");
    return {
      ...eventlog,
      contextText: contextText,
      lastEventTimeProcessed: eventlog.lastEventTime || eventlog.created,
    };
  }

  async function fetchEventlogs(refresh: boolean) {
    const requestId = uuidv4();
    requestIdRef.current = requestId;
    try {
      if (!hasFetched) {
        setIsLoading(true);
      }
      setIsFetching(true);
      const nextPage = nextLink && !refresh ? true : false;
      const url = nextPage ? nextLink! : `${link}/-eventlog`;
      const { data } = await request({ url: url });
      if (requestIdRef.current !== requestId) {
        return;
      }

      let _eventlogs = data.items.map(toTableItem);
      if (nextPage) {
        _eventlogs = [...eventlogs, ...data.items.map(toTableItem)];
      }

      _eventlogs = sortBy(_eventlogs, "lastEventTimeProcessed").toReversed();
      setEventlogs(_eventlogs);
      setNextLink(linksOf(data).next);
      setError("");
      setIsLoading(false);
      setIsFetching(false);
      setHasFetched(true);
    } catch (e) {
      if (requestIdRef.current !== requestId) {
        return;
      }
      setIsLoading(false);
      setIsFetching(false);
      let errorMessage = e?.response?.data?.message;
      if (!errorMessage) errorMessage = e.message;
      setError(errorMessage);
    }
    checkHasWarning();
  }

  React.useEffect(() => {
    checkHasWarning();
  }, [itemLastModified]);

  function checkHasWarning() {
    const lastModifiedDate = moment(itemLastModified);
    const eventlog = eventlogs[0];
    if (!eventlog) {
      setHasWarning(false);
      return;
    }
    const eventDate = moment(eventlog.created);

    const last10MinDate = moment().subtract(10, "minute");
    if (eventDate.isAfter(lastModifiedDate) && eventDate.isAfter(last10MinDate) && eventlog.status === "errored") {
      setHasWarning(true);
      return;
    }

    setHasWarning(false);
  }

  return {
    eventlogs,
    fetchEventlogs,
    nextLink,
    isLoading,
    hasFetched,
    isFetching,
    error,
    hasWarning,
    setItemLastModified,
  };
}
